import { lazy } from "react";
import type { RouteObject } from "react-router-dom";
import { centralPage } from "./guards";

const CentralDashboard = lazy(() =>
  import("@/pages/dashboard/CentralDashboard").then((m) => ({
    default: m.CentralDashboard,
  })),
);
const DoctorsList = lazy(() =>
  import("@/pages/doctors/DoctorsList").then((m) => ({
    default: m.DoctorsList,
  })),
);
const DoctorsLeaderboard = lazy(() =>
  import("@/pages/doctors/DoctorsLeaderboard").then((m) => ({
    default: m.DoctorsLeaderboard,
  })),
);
const DoctorProfile = lazy(() =>
  import("@/pages/doctors/DoctorProfile").then((m) => ({
    default: m.DoctorProfile,
  })),
);
const CreateDoctor = lazy(() =>
  import("@/pages/doctors/CreateDoctor").then((m) => ({
    default: m.CreateDoctor,
  })),
);
const EditDoctor = lazy(() =>
  import("@/pages/doctors/EditDoctor").then((m) => ({ default: m.EditDoctor })),
);
const SessionsList = lazy(() =>
  import("@/pages/sessions/SessionsList").then((m) => ({
    default: m.SessionsList,
  })),
);
const SessionDetail = lazy(() =>
  import("@/pages/sessions/SessionDetail").then((m) => ({
    default: m.SessionDetail,
  })),
);
const CreateSession = lazy(() =>
  import("@/pages/sessions/CreateSession").then((m) => ({
    default: m.CreateSession,
  })),
);
const EditSession = lazy(() =>
  import("@/pages/sessions/EditSession").then((m) => ({
    default: m.EditSession,
  })),
);
const ReviewersList = lazy(() =>
  import("@/pages/reviewers/ReviewersList").then((m) => ({
    default: m.ReviewersList,
  })),
);
const ReviewerDetail = lazy(() =>
  import("@/pages/reviewers/ReviewerDetail").then((m) => ({
    default: m.ReviewerDetail,
  })),
);
const ReviewerSeries = lazy(() =>
  import("@/pages/reviewers/ReviewerSeries").then((m) => ({
    default: m.ReviewerSeries,
  })),
);
const CreateReviewer = lazy(() =>
  import("@/pages/reviewers/CreateReviewer").then((m) => ({
    default: m.CreateReviewer,
  })),
);
const EditReviewer = lazy(() =>
  import("@/pages/reviewers/EditReviewer").then((m) => ({
    default: m.EditReviewer,
  })),
);
const ContractsList = lazy(() =>
  import("@/pages/contracts/ContractsList").then((m) => ({
    default: m.ContractsList,
  })),
);
const SubscriptionIndex = lazy(() =>
  import("@/pages/subscription/SubscriptionIndex").then((m) => ({
    default: m.SubscriptionIndex,
  })),
);
const SubscriptionCheckout = lazy(() =>
  import("@/pages/subscription/SubscriptionCheckout").then((m) => ({
    default: m.SubscriptionCheckout,
  })),
);
const SubscriptionManage = lazy(() =>
  import("@/pages/subscription/SubscriptionManage").then((m) => ({
    default: m.SubscriptionManage,
  })),
);
const SubscriptionRenew = lazy(() =>
  import("@/pages/subscription/SubscriptionRenew").then((m) => ({
    default: m.SubscriptionRenew,
  })),
);
const SubscriptionResult = lazy(() =>
  import("@/pages/subscription/SubscriptionResult").then((m) => ({
    default: m.SubscriptionResult,
  })),
);
const ApiKeysList = lazy(() =>
  import("@/pages/apiKeys/ApiKeysList").then((m) => ({
    default: m.ApiKeysList,
  })),
);
const ApiDocs = lazy(() =>
  import("@/pages/apiDocs/ApiDocs").then((m) => ({ default: m.ApiDocs })),
);
const CentralAdmins = lazy(() =>
  import("@/pages/admins/CentralAdmins").then((m) => ({
    default: m.CentralAdmins,
  })),
);
const CentralRoles = lazy(() =>
  import("@/pages/admins/CentralRoles").then((m) => ({
    default: m.CentralRoles,
  })),
);
const SettingsTabs = lazy(() =>
  import("@/pages/settings/SettingsTabs").then((m) => ({
    default: m.SettingsTabs,
  })),
);
const ActivitiesList = lazy(() =>
  import("@/pages/activities/ActivitiesList").then((m) => ({
    default: m.ActivitiesList,
  })),
);
const ChronicDiseaseConsentPage = lazy(() =>
  import("@/pages/chronic-disease-consent/ChronicDiseaseConsentPage").then(
    (m) => ({ default: m.ChronicDiseaseConsentPage }),
  ),
);

export const centralRoutes: RouteObject[] = [
  { index: true, element: centralPage(<CentralDashboard />) },
  { path: "doctors", element: centralPage(<DoctorsLeaderboard />) },
  {
    path: "doctors/active",
    element: centralPage(<DoctorsList status="active" />),
  },
  {
    path: "doctors/inactive",
    element: centralPage(<DoctorsList status="inactive" />),
  },
  { path: "doctors/new", element: centralPage(<CreateDoctor />) },
  { path: "doctors/:id", element: centralPage(<DoctorProfile />) },
  { path: "doctors/:id/edit", element: centralPage(<EditDoctor />) },
  { path: "sessions", element: centralPage(<SessionsList />) },
  { path: "sessions/new", element: centralPage(<CreateSession />) },
  { path: "sessions/:id/edit", element: centralPage(<EditSession />) },
  { path: "sessions/:id", element: centralPage(<SessionDetail />) },
  { path: "reviewers", element: centralPage(<ReviewersList />) },
  { path: "reviewers/new", element: centralPage(<CreateReviewer />) },
  { path: "reviewers/:id", element: centralPage(<ReviewerDetail />) },
  { path: "reviewers/:id/sessions/series", element: centralPage(<ReviewerSeries />) },
  { path: "reviewers/:id/edit", element: centralPage(<EditReviewer />) },
  { path: "contracts", element: centralPage(<ContractsList />) },
  { path: "subscription", element: centralPage(<SubscriptionIndex />) },
  {
    path: "subscription/checkout",
    element: centralPage(<SubscriptionCheckout />),
  },
  { path: "subscription/manage", element: centralPage(<SubscriptionManage />) },
  { path: "subscription/renew", element: centralPage(<SubscriptionRenew />) },
  { path: "subscription/result", element: centralPage(<SubscriptionResult />) },
  { path: "api-keys", element: centralPage(<ApiKeysList />) },
  { path: "api-docs", element: centralPage(<ApiDocs />) },
  { path: "admins", element: centralPage(<CentralAdmins />) },
  { path: "roles", element: centralPage(<CentralRoles />) },
  { path: "settings", element: centralPage(<SettingsTabs />) },
  { path: "settings/notifications", element: centralPage(<SettingsTabs />) },
  { path: "activities", element: centralPage(<ActivitiesList />) },
  {
    path: "chronic-disease-consent",
    element: centralPage(<ChronicDiseaseConsentPage />),
  },
];
